import { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { CoreContext } from "../../context/global/CoreContext";
import axios from "axios";
import {
  getCurrentHostname,
  isAccessingViaIpAddress,
} from "../../utils/common/environment";
import { getGuestLoginRoute } from "../../utils/auth";

/**
 * Whether ``userData`` satisfies the optional role gate (OR of requested roles).
 *
 * @param {Object|null|undefined} userData Current user row.
 * @param {Array<string>} requiredRoles Role keys to accept (empty = any authenticated user).
 * @returns {boolean}
 */
function userHasAccess(userData, requiredRoles) {
  if (!userData) {
    return false;
  }
  if (!requiredRoles.length) {
    return true;
  }
  const userRoles = {
    executive: userData.executive,
    manager: userData.manager,
    staff: userData.staff,
    admin: userData.admin,
  };
  return requiredRoles.some((role) => userRoles[role]);
}

/**
 * A higher-order component that checks if the user is authenticated.
 * If the user is not authenticated, they are redirected to the login page.
 *
 * Optional role props are OR'd: the user needs any one of the requested roles.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render if the user is authenticated.
 * @param {boolean} [props.manager=false] - Whether the manager role is accepted.
 * @param {boolean} [props.executive=false] - Whether the executive role is accepted.
 * @param {boolean} [props.staff=false] - Whether the staff role is accepted.
 * @param {boolean} [props.admin=false] - Whether the admin role is accepted.
 * @returns {React.ReactNode} The child components if authenticated, otherwise redirects to login.
 */
const RequireAuth = ({
  children,
  manager = false,
  executive = false,
  staff = false,
  admin = false,
}) => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const { user, userLoading, userError, refreshUser } = useContext(CoreContext);

  const requiredRoles = useMemo(
    () =>
      [
        manager ? "manager" : null,
        executive ? "executive" : null,
        staff ? "staff" : null,
        admin ? "admin" : null,
      ].filter(Boolean),
    [manager, executive, staff, admin]
  );

  useEffect(() => {
    // Check if we're on a mobile device on local network (IP address rather than localhost)
    const isMobileOnLocalNetwork = isAccessingViaIpAddress();

    // Get hostname from URL
    const currentHost = getCurrentHostname();

    // Force axios to use the correct IP instead of 127.0.0.1 when on mobile
    if (isMobileOnLocalNetwork && axios.defaults.baseURL?.includes("127.0.0.1")) {
      // Replace 127.0.0.1 with the actual IP address
      axios.defaults.baseURL = axios.defaults.baseURL.replace(
        "127.0.0.1",
        currentHost
      );
    }

    /**
     * Authorizes from CoreContext user, refreshes if needed, or redirects to login.
     *
     * @returns {Promise<void>}
     */
    const checkAuth = async () => {
      try {
        // If user data is already loaded from context
        if (user && !userLoading) {
          if (userHasAccess(user, requiredRoles)) {
            setIsAuthorized(true);
          } else {
            navigate("/not-authorized");
          }
        }
        // If there was an error loading the user or the user isn't loaded yet
        else if (userError || !user) {
          try {
            // Try to refresh the user data
            if (!userLoading) {
              console.log("Attempting to refresh user with correct API URL");
              const userData = await refreshUser();
              if (userHasAccess(userData, requiredRoles)) {
                setIsAuthorized(true);
              } else {
                navigate("/not-authorized");
              }
            }
          } catch (error) {
            // If refreshing fails, redirect to login
            const currentPath =
              window.location.pathname + window.location.search;

            navigate(getGuestLoginRoute(currentPath));
          }
        }
      } catch (error) {
        const currentPath = window.location.pathname + window.location.search;

        navigate(getGuestLoginRoute(currentPath));
      }
    };

    checkAuth();
  }, [
    navigate,
    requiredRoles,
    user,
    userLoading,
    userError,
    refreshUser,
  ]);

  // Show nothing while checking authorization
  if (userLoading) {
    return null; // Or a loading spinner if preferred
  }

  return isAuthorized ? children : null;
};

export default RequireAuth;
